import { useCallback, useRef, useState } from "react";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "@/components/state/store";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { useAssignmentLeaveGuard } from "@/components/appointmentRequest/assignmentPlanner/useAssignmentLeaveGuard";
import { useRegisterUnsavedChanges } from "@/context/unsavedChangesContext";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useAssignmentPlan } from "@/components/appointmentRequest/assignmentPlanner/useAssignmentPlan";
import {
  assignEmployee,
  canClearEmployee,
  employeeInSlot,
  PlannerSlot,
  slotDay,
  slotUnavailableReason,
} from "@/components/appointmentRequest/assignmentPlanner/assignmentPlan";
import PlannerSlotWorkspace from "@/components/appointmentRequest/assignmentPlanner/plannerSlotWorkspace";
import PlannerSlotPopover from "@/components/appointmentRequest/assignmentPlanner/plannerSlotPopover";
import PlannerEmployeeDialog from "@/components/appointmentRequest/assignmentPlanner/plannerEmployeeDialog";
import PlannerIcon from "@/components/appointmentRequest/assignmentPlanner/plannerIcon";
import PlannerChangeTable, {
  PlannerChange,
} from "@/components/appointmentRequest/assignmentPlanner/plannerChangeTable";
import styles from "@/components/appointmentRequest/assignmentPlanner/planner.module.css";

export default function ManagerAssignmentPlanner() {
  const { t } = useTranslation();
  usePageHeading(t("planner.title"));
  const { appointmentrequestId = "" } = useParams();
  const companyId = useSelector((state: RootState) => state.parent.subjectId);
  const state = useAssignmentPlan(appointmentrequestId, companyId);
  const { snapshot, plan, changes, update } = state;
  const [selectedDay, setDay] = useState<string>();
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [slotPicker, setSlotPicker] = useState<{
    slot: PlannerSlot;
    anchor: HTMLElement;
  }>();
  const [review, setReview] = useState(false);
  const [saved, setSaved] = useState(false);
  const openSlot = useCallback((slot: PlannerSlot, anchor: HTMLElement) => {
    setSlotPicker({ slot, anchor });
  }, []);
  const clearEmployee = useCallback(
    (id: string) => {
      const employee = snapshot?.employees.find((entry) => entry.id === id);
      if (employee && canClearEmployee(employee)) {
        update({ ...plan, [id]: null });
        setSaved(false);
      }
    },
    [snapshot, plan, update],
  );
  const confirmingAssignment = useRef(false);
  const dirty = changes.length > 0 || state.saving;
  useAssignmentLeaveGuard(dirty);
  useRegisterUnsavedChanges(`appointment-plan-${appointmentrequestId}`, dirty);
  const { showConfirmationModalAsync } = useConfirmationModal();
  const navigate = useNavigate();
  const firstSlot =
    snapshot?.slots.find(
      (slot) => !slotUnavailableReason(slot) && !slot.occupied,
    ) ??
    snapshot?.slots.find((slot) => !slotUnavailableReason(slot)) ??
    snapshot?.slots[0];
  const day =
    selectedDay ??
    (firstSlot ? slotDay(firstSlot) : dayjs().format("YYYY-MM-DD"));
  const assigned =
    snapshot?.employees.filter((employee) => plan[employee.id]).length ?? 0;
  const complete = Boolean(
    snapshot?.employees.length && assigned === snapshot.employees.length,
  );
  const assign = async (
    employeeId: string,
    slotId: string,
    replace = false,
  ): Promise<boolean> => {
    if (!snapshot || state.saving || confirmingAssignment.current) return false;
    const next = assignEmployee(snapshot, plan, employeeId, slotId, replace);
    if (next === plan) return false;
    const employee = snapshot.employees.find(
      (entry) => entry.id === employeeId,
    )!;
    const slot = snapshot.slots.find((entry) => entry.id === slotId)!;
    const previousId = plan[employeeId] ?? employee.assignment?.slot_id;
    const occupant = snapshot.employees.find(
      (entry) => entry.id === employeeInSlot(plan, slotId),
    );
    const moving = previousId && previousId !== slotId;
    const replacing = occupant && occupant.id !== employeeId;
    if (moving || replacing) {
      const pendingChanges: PlannerChange[] = [
        {
          subject_id: employeeId,
          previous_slot_id: previousId ?? null,
          slot_id: slotId,
        },
      ];
      if (replacing)
        pendingChanges.push({
          subject_id: occupant.id,
          previous_slot_id: slotId,
          slot_id: null,
        });
      confirmingAssignment.current = true;
      try {
        if (
          !(await showConfirmationModalAsync(
            <div className={styles.confirmationMessage}>
              <p>{t("planner.change_help")}</p>
              {replacing && (
                <Alert severity="warning">
                  {t("planner.replacement_warning", { name: occupant.name })}
                </Alert>
              )}
              <PlannerChangeTable
                snapshot={snapshot}
                changes={pendingChanges}
              />
            </div>,
            {
              title: t("planner.confirm_change_title"),
              confirmButtonText: t("planner.confirm_change"),
            },
          ))
        )
          return false;
      } finally {
        confirmingAssignment.current = false;
      }
    }
    state.update(next);
    setDay(slotDay(slot));
    setSaved(false);
    setSlotPicker(undefined);
    return true;
  };
  const discard = async (reload: boolean) => {
    if (
      dirty &&
      !(await showConfirmationModalAsync(t("planner.leave_warning")))
    )
      return;
    setReview(false);
    setSlotPicker(undefined);
    setEmployeeDialogOpen(false);
    state.discard();
    if (reload) {
      setDay(undefined);
      state.refresh();
    }
  };
  if (state.loading) return null;
  if (!snapshot)
    return (
      <Alert severity="error">
        {t("planner.load_failed")}{" "}
        <GenericButton onClick={state.refresh}>{t("retry")}</GenericButton>
      </Alert>
    );
  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div>
          <h2>{snapshot.title}</h2>
        </div>
        <div className={styles.tools}>
          <GenericButton
            variant="outlined"
            disabled={state.saving || !state.canUndo}
            onClick={state.undo}
          >
            {t("planner.undo")}
          </GenericButton>
          <GenericButton
            variant="outlined"
            disabled={state.saving || !changes.length}
            onClick={() => void discard(false)}
          >
            {t("planner.discard")}
          </GenericButton>
          <GenericButton
            variant="outlined"
            disabled={state.saving}
            onClick={() => void discard(true)}
          >
            {t("planner.reload")}
          </GenericButton>
          <GenericButton
            variant="outlined"
            disabled={state.saving}
            onClick={() => navigate("/appointmentrequestopen")}
          >
            {t("back_to_appointments")}
          </GenericButton>
          <GenericButton
            disabled={state.saving}
            startIcon={<PlannerIcon name="users" />}
            title={t("planner.view_employees")}
            aria-haspopup="dialog"
            aria-expanded={employeeDialogOpen}
            onClick={() => {
              setSlotPicker(undefined);
              setEmployeeDialogOpen(true);
            }}
          >
            {t("planner.employees")}
          </GenericButton>
        </div>
      </div>
      <div className={styles.assignmentProgress}>
        <div className={styles.progress} role="status">
          <strong>
            {t("planner.progress", {
              assigned,
              total: snapshot.employees.length,
            })}
          </strong>
          <span>
            {t("planner.remaining", {
              count: snapshot.employees.length - assigned,
            })}
          </span>
        </div>
        <LinearProgress
          variant="determinate"
          value={
            snapshot.employees.length
              ? (100 * assigned) / snapshot.employees.length
              : 0
          }
        />
      </div>
      {complete && <Alert severity="success">{t("planner.complete")}</Alert>}
      {saved && <Alert severity="success">{t("planner.saved")}</Alert>}
      {state.error != null && (
        <Alert severity="error" role="alert">
          {t("planner.save_failed")}
          <div>
            {state.error instanceof Error
              ? state.error.message
              : typeof state.error === "string"
              ? state.error
              : JSON.stringify(state.error)}
          </div>
        </Alert>
      )}
      <fieldset disabled={state.saving} className={styles.workspace}>
        <div className={styles.plannerBoard}>
          <PlannerSlotWorkspace
            sideBySide
            onDay={setDay}
            snapshot={snapshot}
            plan={plan}
            day={day}
            complete={complete}
            onOpen={openSlot}
            onClear={clearEmployee}
          />
        </div>
      </fieldset>
      <div className={styles.footer}>
        {saved && (
          <GenericButton variant="outlined" onClick={() => navigate("/")}>
            {t("back_to_home")}
          </GenericButton>
        )}
        <GenericButton
          disabled={!changes.length || state.saving}
          onClick={() => setReview(true)}
        >
          {t("planner.review")}
        </GenericButton>
      </div>
      {employeeDialogOpen && (
        <PlannerEmployeeDialog
          snapshot={snapshot}
          plan={plan}
          initialDay={day}
          onAssign={(employeeId, slotId) => assign(employeeId, slotId, true)}
          onClose={() => setEmployeeDialogOpen(false)}
        />
      )}
      <PlannerSlotPopover
        open={Boolean(slotPicker)}
        anchor={slotPicker?.anchor}
        slot={slotPicker?.slot}
        snapshot={snapshot}
        plan={plan}
        onChoose={(id) => {
          if (slotPicker) void assign(id, slotPicker.slot.id, true);
        }}
        onClose={() => setSlotPicker(undefined)}
      />
      <Dialog
        open={review}
        fullWidth
        maxWidth="md"
        onClose={state.saving ? undefined : () => setReview(false)}
        aria-labelledby="planner-review-title"
      >
        <DialogTitle id="planner-review-title">
          {t("planner.review")}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
            "& > .MuiAlert-root": { flexShrink: 0 },
          }}
        >
          <Alert severity="info">{t("planner.review_help")}</Alert>
          <PlannerChangeTable snapshot={snapshot} changes={changes} />
          {state.error != null && (
            <Alert severity="error">{t("planner.save_failed")}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <GenericButton
            variant="outlined"
            disabled={state.saving}
            onClick={() => setReview(false)}
          >
            {t("back")}
          </GenericButton>
          <GenericButton
            disabled={state.saving || !changes.length}
            onClick={() =>
              state.confirm(() => {
                setReview(false);
                setSaved(true);
              })
            }
          >
            {state.saving ? t("loading") : t("planner.confirm")}
          </GenericButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
